import { useQuery } from "@tanstack/react-query";
import getConfig from "@/utils/getConfig";
import { DevourPlayApi } from "@devour/client";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";

interface GameQuestData {
    gameInfo: {
        _id: string; // MongoDB ObjectId for new quest system
        gameId: number; // Overwolf gameId for legacy compatibility
        name: string;
        slug: string;
        isEventSupported: boolean;
        questCount: number;
    };
    quests: Array<{
        _id: string;
        name: string;
        description: string;
        gameId: number;
        target: number;
        xp: number;
        questReset: string;
        rewardType: string;
        isActive: boolean;
        onAllSupportedGames: boolean;
        milestones?: any[];
        rewardConfigs?: any;
        userProgress: {
            progress: number;
            isClaimed: boolean;
            hasRecentClaims: boolean;
            claimedMilestoneIndexes: number[];
        };
    }>;
}

interface AllGamesQuestsResponse {
    success: boolean;
    summary: {
        totalGames: number;
        totalQuests: number;
        gamesWithProgress: number;
    };
    games: Record<string, GameQuestData>;
    timestamp: string;
}

/**
 * Fetches all games with their quests and user progress in a single optimized call
 * Replaces 63+ individual API calls with one aggregated endpoint
 */
export const useAllGamesQuests = () => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useQuery<AllGamesQuestsResponse>({
        queryKey: ["all-games-quests", fullToken?.id],
        queryFn: async () => {
            const data = await new DevourPlayApi(getConfig(fullToken)).getDevourplayAllGamesQuests();
            return data as unknown as AllGamesQuestsResponse;
        },
        enabled: !!fullToken, // Only run if authenticated
        staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
        gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes (formerly cacheTime)
        refetchOnWindowFocus: false, // Don't refetch on window focus to prevent spikes
        refetchOnMount: false, // Use WebSocket for real-time updates instead
        retry: 2, // Retry failed requests twice
        retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    });
};

/**
 * Helper hook to get data for a specific game
 */
export const useGameQuests = (gameId: number | string | undefined) => {
    const { data, ...rest } = useAllGamesQuests();

    if (!gameId) {
        return {
            data: null,
            ...rest,
        };
    }

    const gameData = data?.games?.[String(gameId)];

    return {
        data: gameData,
        ...rest,
    };
};

/**
 * Helper hook to get all games list
 */
export const useAllGamesList = () => {
    const { data, ...rest } = useAllGamesQuests();

    const gamesList = data ? Object.values(data.games).map(g => g.gameInfo) : [];

    return {
        data: gamesList,
        ...rest,
    };
};